
import React from 'react'
import { Rating } from 'primereact/rating'
import { Tag } from 'primereact/tag'
import { Button } from 'primereact/button'
import { Movie } from '../../api/movies'
import { useTranslation } from 'react-i18next'

interface MovieCardProps {
    movie: Movie
}

const MovieCard: React.FC<MovieCardProps> = ({ movie }) => {
    const { i18n } = useTranslation()

    const getMovieTitle = (movie: Movie) => {
        return i18n.language === 'ta' ? movie.titleTamil : i18n.language === 'de' ? movie.titleGerman : movie.title
    }

    const getMovieGenre = (movie: Movie) => {
        return i18n.language === 'ta' ? movie.genreTamil : i18n.language === 'de' ? movie.genreGerman : movie.genre
    }

    const header = (
        <div className="relative overflow-hidden border-round-top-2xl cursor-pointer group" style={{ height: '420px' }}>
            <img
                alt={getMovieTitle(movie)}
                src={movie.posterUrl}
                onError={(e) => {
                    e.currentTarget.src = 'https://images.unsplash.com/photo-1485846234645-a62644f84728?q=80&w=600&auto=format&fit=crop'
                }}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                className="transition-transform transition-duration-500 hover:scale-110 h-full w-full"
            />

            {/* Overlay Gradient on Hover */}
            <div className="absolute top-0 left-0 w-full h-full bg-black-alpha-0 hover:bg-black-alpha-40 transition-colors transition-duration-300 z-1"></div>

            <div className="absolute top-0 right-0 p-3 z-2">
                <Tag value={movie.rating} icon="pi pi-star-fill" className="font-bold text-lg px-2 py-1 shadow-4 border-1 border-gray-600" style={{ backgroundColor: 'rgba(0,0,0,0.7)', color: '#EAB308' }} rounded></Tag>
            </div>
            {movie.isNewRelease && (
                <div className="absolute top-0 left-0 p-3 z-2">
                    <Tag value="New" className="bg-red-600 text-white font-bold text-lg px-2 py-1 shadow-4" rounded></Tag>
                </div>
            )}

            <div className="absolute bottom-0 left-0 w-full h-15rem bg-gradient-to-t from-gray-900 via-gray-900 to-transparent opacity-95 z-2"></div>

            <div className="absolute bottom-0 left-0 w-full p-4 z-3">
                <div className="flex align-items-center justify-content-between mb-3">
                    <span className="text-sm text-white font-bold border-1 border-gray-600 px-3 py-1 border-round-2xl bg-black-alpha-70 backdrop-blur-md shadow-2">{movie.year}</span>
                    <div className="bg-black-alpha-70 border-round-2xl px-2 py-1 border-1 border-gray-600 backdrop-blur-md shadow-2 flex align-items-center">
                        <Rating value={Math.round((movie.rating || 0) / 2)} readOnly stars={5} cancel={false} className="stars-yellow" pt={{
                            item: { className: 'text-yellow-500' },
                            onIcon: { className: 'text-yellow-500' },
                            offIcon: { className: 'text-gray-400' }
                        }} />
                    </div>
                </div>

                <div className="text-2xl font-bold mb-2 text-white white-space-nowrap overflow-hidden text-overflow-ellipsis text-shadow-1" title={getMovieTitle(movie)}>
                    {getMovieTitle(movie)}
                </div>
                <div className="mb-4">
                    <span className="text-sm text-orange-400 font-medium uppercase tracking-wider">{getMovieGenre(movie)}</span>
                </div>

                <Button
                    label="Watch Now"
                    icon="pi pi-play"
                    className="w-full font-bold text-lg py-3 shadow-6 hover:shadow-8 transition-all transition-duration-200 border-none flex align-items-center gap-2"
                    style={{
                        background: 'linear-gradient(90deg, #ea580c 0%, #f97316 100%)',
                        boxShadow: '0 8px 20px rgba(234, 88, 12, 0.4)',
                        borderRadius: '50px',
                        justifyContent: 'center',
                        paddingLeft: '2rem',
                        paddingRight: '2rem'
                    }}
                />
            </div>
        </div>
    )

    return (
        <div className="col-12 sm:col-6 md:col-4 lg:col-3 p-3">
            <div className="h-full border-round-2xl transition-all transition-duration-300 overflow-hidden bg-gray-900 shadow-4 hover:shadow-8 hover:-translate-y-2" style={{ border: '1px solid rgba(255,255,255,0.05)' }}>
                {header}
            </div>
        </div>
    )
}

export default MovieCard
